import { useRef } from 'react';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { OutlineButton } from 'components/buttons/OutlineButton';

const getContrastTextColor = hexColor => {
  if (typeof hexColor !== 'string') return '#FFFFFF';

  const hex = hexColor.replace('#', '');
  if (hex.length !== 6) {
    return '#FFFFFF';
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#373737' : '#FFFFFF';
};

const expand3CharHex = h => {
  const hex = h.replace('#', '');
  return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
};

const normalizeHex = value => {
  if (typeof value !== 'string') {
    return '#ffffff';
  }

  const hex = value.trim();

  if (/^#([0-9a-fA-F]{6})$/.test(hex)) {
    return hex;
  }

  if (/^#([0-9a-fA-F]{3})$/.test(hex)) {
    return expand3CharHex(hex);
  }

  return '#ffffff';
};

const ColorField = ({ label, value, onChange }) => {
  const theme = useTheme();
  const colorInputRef = useRef(null);

  const safeLabel = String(label ?? '');
  const safeValue = String(value ?? '');
  const pickerColor = normalizeHex(safeValue);

  const handleOpenColorPicker = e => {
    e.stopPropagation();

    if (colorInputRef.current) {
      if (typeof colorInputRef.current.showPicker === 'function') {
        colorInputRef.current.showPicker();
      } else {
        colorInputRef.current.click();
      }
    }
  };

  const handleColorChange = e => {
    const hex = e.target.value;

    if (onChange) {
      onChange(hex);
    }
  };

  return (
    <Box sx={{ marginBottom: '12px' }}>
      <Typography
        sx={{
          fontSize: theme.typography.customFontSize[1],
          color: theme.palette.primary.main,
          fontWeight: theme.typography.customFontWeight[1],
          marginBottom: '6px',
        }}
      >
        {safeLabel}
      </Typography>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <OutlineButton
          variant="outlined"
          size="small"
          onClick={handleOpenColorPicker}
          sx={{
            height: '28px',
            width: '100%',
            fontSize: theme.typography.customFontSize[1],
            borderRadius: theme.shape.customBorderRadius[0],
            borderColor: theme.palette.primary.greyFifth,
            backgroundColor: safeValue || 'transparent',
            color: getContrastTextColor(safeValue),
            '&:hover': {
              borderColor: theme.palette.primary.greyFifth,
              backgroundColor: safeValue || 'transparent',
              color: getContrastTextColor(safeValue),
            },
          }}
        >
          {safeValue}
        </OutlineButton>

        <input
          ref={colorInputRef}
          type="color"
          value={pickerColor}
          onChange={handleColorChange}
          tabIndex={-1}
          aria-hidden="true"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Box>
  );
};

export default ColorField;
